import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConnectors } from '@/core/hooks'
import { useConnections } from '@/core/connection/connection.context'
import { EmptyState, ErrorNotice, Loading, Logo } from '@/shared/components'
import type { ConnectorSummary } from '@/core/types/api.types'
import { Constellation } from '@/shared/components/Constellation'

export function CatalogPage() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string | null>(null)

  const catalog = useConnectors()
  const { byConnector } = useConnections()
  const navigate = useNavigate()

  const connectors = useMemo(() => {
    const all = catalog.data?.connectors ?? []
    const needle = search.trim().toLowerCase()
    return all.filter((c) => {
      if (category && c.category !== category) return false
      if (!needle) return true
      return (
        c.name.toLowerCase().includes(needle) ||
        c.description.toLowerCase().includes(needle) ||
        c.category.toLowerCase().includes(needle)
      )
    })
  }, [catalog.data, search, category])

  const categories = catalog.data?.categories ?? []

  return (
    <div className="page">
      <div className="page-header hero">
        <Constellation />
        <div className="hero-text">
          <h1>Connectors</h1>
          <p>Bring your tools and data together. Choose an application to get started.</p>
        </div>
      </div>

      <div className="stack" style={{ gap: 13, marginBottom: 18 }}>
        <input
          className="input"
          placeholder="Search apps by name or category"
          aria-label="Search connectors"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="chiprow">
          <button
            className="chip-filter"
            data-on={category === null}
            aria-pressed={category === null}
            onClick={() => setCategory(null)}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className="chip-filter"
              data-on={category === cat}
              aria-pressed={category === cat}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {catalog.isLoading && <Loading label="Loading connectors…" />}
      {catalog.error ? (
        <ErrorNotice error={catalog.error} onRetry={() => catalog.refetch()} />
      ) : null}

      {catalog.data && connectors.length === 0 && (
        <EmptyState title="Nothing matches that search" action={<button className="btn" onClick={() => { setSearch(''); setCategory(null) }}>Clear filters</button>}>
          Try a different term, or clear the category filter.
        </EmptyState>
      )}

      {catalog.data && <p className="catalog-count muted small" role="status">{connectors.length} application{connectors.length === 1 ? '' : 's'}{category ? ` in ${category}` : ' available'}</p>}
      <div className="applist catalog-grid">
        {connectors.map((connector) => (
          <AppRow
            key={connector.id}
            connector={connector}
            connectionCount={byConnector[connector.id]?.length ?? 0}
            onConnect={() => navigate(`/connections/new/${connector.id}`)}
            onOpen={() => navigate(`/catalog/${connector.id}`)}
          />
        ))}
      </div>

    </div>
  )
}

function AppRow({
  connector,
  connectionCount,
  onConnect,
  onOpen,
}: {
  connector: ConnectorSummary
  connectionCount: number
  onConnect: () => void
  onOpen: () => void
}) {
  const usable = connector.auth_methods.filter((m) => m.ready)
  // Only genuinely unusable when *no* method works — a connector that also
  // accepts a pasted token is fine without an OAuth app.
  const blocked = usable.length === 0

  return (
    <div className="approw catalog-card" role="group" aria-label={connector.name}>
      <Logo src={connector.logo} name={connector.name} id={connector.id} />

      <div className="approw-body">
        <div className="approw-name">
          {connector.name}
          {connectionCount > 0 && (
            <span className="badge badge-ok">{connectionCount} connected</span>
          )}
          {blocked && <span className="badge badge-warn">setup needed</span>}
        </div>
        <div className="approw-meta">
          <span>{connector.category}</span>
          <span>
            {connector.action_count} action{connector.action_count === 1 ? '' : 's'}
          </span>
          {connector.auth_method_count > 1 && (
            <span>{connector.auth_method_count} ways to connect</span>
          )}
        </div>
      </div>

      <p className="catalog-description">{connector.description}</p>

      <div className="catalog-card-actions">
      <button
        className="btn btn-ghost btn-sm"
        onClick={(e) => {
          e.stopPropagation()
          onOpen()
        }}
        title="View actions and details"
      >
        Details
      </button>
      <button className="btn btn-sm" onClick={onConnect} disabled={blocked}>
        {connectionCount > 0 ? 'Add connection' : 'Connect'}
      </button>
      </div>
    </div>
  )
}
